import { ProdRow, emptyProdRow } from "../components/OSProductivityTable";
import { RAZMER_STAGE_ID } from "../components/OSStageTree";
import { MachineOption } from "../select/MachineSelect";
import { SubprocessOption } from "../select/SubprocessCells";
import { buildSubprocessOrderIndex } from "./subprocessOrder";
import { isPerSubprocessStage, unmatchedActualRows } from "./logbookStage";

// Machine montage/dismount seed frozen into the RN's normativSnapshot at creation
// (server/utils/rnPlannedMinutes.js buildMachineSeeds). When present it replaces the live
// machine codebook, so a later /machines edit cannot change an existing RN's plan.
export interface RNMachineSeed {
  isRazmer?: boolean;
  subprocess?: string | null;
  machine: string;
  setupTime?: number | null;
  cleaningDismountTime?: number | null;
  operatorCount?: number | null;
  order?: number;
}

export const machineSeedsForStage = (seeds: RNMachineSeed[], stageId: string): RNMachineSeed[] => seeds
  .filter((seed) => (stageId === RAZMER_STAGE_ID
    ? !!seed.isRazmer
    : !seed.isRazmer && String(seed.subprocess || "") === stageId))
  .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

interface RNTotalProductivityParams {
  osProductivity: ProdRow[];
  productivityRows: ProdRow[];
  selectedSubprocessIds: string[];
  subprocesses: SubprocessOption[];
  razmerProcessMachineIds: string[];
  machineOptions: MachineOption[];
  machineSeeds?: RNMachineSeed[];
}

// Shared RN Log Book projection. Keep this in lockstep with the per-stage RN view:
// planned normativ rows are overlaid with saved actual timestamps, then grouped in
// the canonical Размерување/subprocess order.
export const deriveRNTotalProductivityRows = ({
  osProductivity,
  productivityRows,
  selectedSubprocessIds,
  subprocesses,
  razmerProcessMachineIds,
  machineOptions,
  machineSeeds,
}: RNTotalProductivityParams): ProdRow[] => {
  const machineById = new Map(machineOptions.map((machine) => [machine._id, machine]));
  const defaultProductivityRow = (machineId: string, subprocessId: string): ProdRow => {
    const machine = machineSeeds
      ? machineSeedsForStage(machineSeeds, subprocessId).find((seed) => seed.machine === machineId)
      : machineById.get(machineId);
    const asStr = (value: number | null | undefined) => value === null || value === undefined ? "" : String(value);
    return {
      ...emptyProdRow(),
      machineId,
      montageTime: asStr(machine?.setupTime ?? null),
      dismountCleanTime: asStr(machine?.cleaningDismountTime ?? null),
      operatorCount: asStr(subprocesses.find((sp) => sp._id === subprocessId)?.operatorCount ?? null),
      subprocessId,
    };
  };
  const mergeProdActual = (os: ProdRow, saved?: ProdRow): ProdRow => ({
    ...emptyProdRow(),
    ...os,
    extraSetupParams: saved?.extraSetupParams ?? [],
    montageStart: saved?.montageStart ?? "",
    montageEnd: saved?.montageEnd ?? "",
    machineStart: saved?.machineStart ?? "",
    machineEnd: saved?.machineEnd ?? "",
    dismountStart: saved?.dismountStart ?? "",
    dismountEnd: saved?.dismountEnd ?? "",
    cleaningStart: saved?.cleaningStart ?? "",
    cleaningEnd: saved?.cleaningEnd ?? "",
  });
  const machineIdsForStage = (stageId: string): string[] => {
    if (machineSeeds) return machineSeedsForStage(machineSeeds, stageId).map((seed) => seed.machine);
    if (stageId === RAZMER_STAGE_ID) return razmerProcessMachineIds;
    const subprocess = subprocesses.find((sp) => sp._id === stageId);
    if (!subprocess?.machines) return [];
    const seen = new Set<string>();
    return [...subprocess.machines]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((row) => (row.machine && typeof row.machine === "object") ? row.machine._id : row.machine)
      .filter((id): id is string => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  };
  const planProdRowsForStage = (stageId: string): ProdRow[] => {
    const fromOs = osProductivity.filter((p) => p.subprocessId === stageId);
    if (isPerSubprocessStage(fromOs, stageId)) return fromOs;
    const existing = new Set(fromOs.map((r) => r.machineId).filter(Boolean));
    const seeded = machineIdsForStage(stageId)
      .filter((id) => !existing.has(id))
      .map((id) => defaultProductivityRow(id, stageId));
    return [...fromOs, ...seeded];
  };
  const hasContent = (r: ProdRow) => !!(
    r.machineId || r.montageTime || r.machineTime || r.cleaningTime || r.dismountCleanTime || r.operatorCount
    || r.montageStart || r.montageEnd || r.machineStart || r.machineEnd
    || r.dismountStart || r.dismountEnd || r.cleaningStart || r.cleaningEnd
  );
  const mergeSaved = (os: ProdRow) => mergeProdActual(
    os,
    productivityRows.find((r) => r.subprocessId === os.subprocessId && r.machineId === os.machineId)
  );
  const stageIds = [RAZMER_STAGE_ID, ...selectedSubprocessIds];
  const planned = stageIds.flatMap((id) => {
    const plan = planProdRowsForStage(id);
    const orphans = isPerSubprocessStage(plan, id)
      ? unmatchedActualRows(id, plan, productivityRows, mergeProdActual, emptyProdRow)
      : [];
    const rows = [...plan.map(mergeSaved), ...orphans].filter(hasContent);
    if (rows.length > 0) return rows;
    return id === RAZMER_STAGE_ID ? [] : [{ ...emptyProdRow(), subprocessId: id }];
  });
  const leftovers = osProductivity
    .filter((p) => p.subprocessId !== RAZMER_STAGE_ID && !selectedSubprocessIds.includes(p.subprocessId))
    .map(mergeSaved)
    .filter(hasContent);
  const orderIndex = buildSubprocessOrderIndex(subprocesses, RAZMER_STAGE_ID);
  return [...planned, ...leftovers]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const ao = orderIndex.get(a.row.subprocessId) ?? Number.MAX_SAFE_INTEGER;
      const bo = orderIndex.get(b.row.subprocessId) ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return a.index - b.index;
    })
    .map(({ row }) => row);
};
